import { useEffect, useMemo, useRef, useState } from 'react';

import ModalShell from '../components/ModalShell.jsx';
import { formatTemperature } from '../dashboard/formatZone.js';
import {
  HPI_STEP,
  pairedAddPrimaryRanges,
  pairedAddSecondaryRanges,
  pairedEditPrimaryRange,
  pairedEditSecondaryRange,
  timeSelectOptions,
  isValidPairedAdd,
} from './scheduleEditModel.js';

// A modal editor for a heat-pump (HPI) PAIRED schedule event. The user picks two
// times: when the active period starts (Comfort for heating, On for hot water)
// and when it ends / the following period starts (Setback / Off). The value is
// fixed — heating runs the zone's comfort/reduced setpoints, hot water is simply
// On then Off — so there is no temperature or on/off control here, only the two
// times, constrained by darwin_core's paired allowed-time ranges (10-min step).
export default function PairedEventEditorModal({
  mode,
  day,
  kind,
  working,
  is24Hour,
  hpTemps,
  existing,
  canDelete,
  onSave,
  onDelete,
  onClose,
}) {
  const isEdit = mode === 'edit';
  const isHeating = kind === 'hpiHeating';
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [primaryStart, setPrimaryStart] = useState(() => {
    if (isEdit) {
      return existing?.primaryStart;
    }
    const opts = timeSelectOptions(
      pairedAddPrimaryRanges(working, day),
      HPI_STEP,
      is24Hour,
    );
    return opts[0]?.value;
  });
  const [secondaryStart, setSecondaryStart] = useState(() =>
    isEdit ? existing?.secondaryStart : undefined,
  );

  // In add mode the secondary range depends on the primary but the primary range
  // does NOT depend on the secondary (darwin_core's availableStartTimeRanges has
  // no such input), so a secondary chosen for one primary can silently become
  // invalid when the primary changes. Clear it on a later primary change so the
  // user re-picks against the new range (Save is also gated on validity below).
  const firstRun = useRef(true);
  useEffect(() => {
    if (isEdit) {
      return;
    }
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setSecondaryStart(undefined);
  }, [isEdit, primaryStart]);

  const primaryOptions = useMemo(() => {
    const ranges = isEdit
      ? pairedEditPrimaryRange(
          working,
          day,
          existing.primaryStart,
          secondaryStart,
        )
      : pairedAddPrimaryRanges(working, day);
    return timeSelectOptions(ranges, HPI_STEP, is24Hour);
  }, [isEdit, working, day, secondaryStart, existing, is24Hour]);

  const secondaryOptions = useMemo(() => {
    if (primaryStart === undefined) {
      return [];
    }
    const ranges = isEdit
      ? pairedEditSecondaryRange(
          working,
          day,
          primaryStart,
          existing.secondaryStart,
        )
      : pairedAddSecondaryRanges(working, day, primaryStart);
    return timeSelectOptions(ranges, HPI_STEP, is24Hour);
  }, [isEdit, working, day, primaryStart, existing, is24Hour]);

  const canSave = isEdit
    ? primaryStart !== undefined && secondaryStart !== undefined
    : isValidPairedAdd(working, day, primaryStart, secondaryStart);

  const primaryCaption = isHeating
    ? `Comfort${hpTemps?.comfortSetpoint ? ` · ${formatTemperature(hpTemps.comfortSetpoint)}` : ''}`
    : 'On';
  const secondaryCaption = isHeating
    ? `Setback${hpTemps?.reducedSetpoint ? ` · ${formatTemperature(hpTemps.reducedSetpoint)}` : ''}`
    : 'Off';

  const title = isEdit ? 'Edit event' : `Add event · ${day}`;

  return (
    <ModalShell labelledBy="paired-modal-title" onClose={onClose}>
      <h2 id="paired-modal-title" className="schedule-modal__title">
        {title}
      </h2>

      <div className="schedule-field">
        <label className="schedule-field__label" htmlFor="paired-primary">
          {primaryCaption} starts
        </label>
        <select
          id="paired-primary"
          className="schedule-field__select"
          value={primaryStart ?? ''}
          onChange={(e) => setPrimaryStart(Number(e.target.value))}
        >
          {primaryOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="schedule-field">
        <label className="schedule-field__label" htmlFor="paired-secondary">
          {secondaryCaption} starts
        </label>
        <select
          id="paired-secondary"
          className="schedule-field__select"
          value={secondaryStart ?? ''}
          disabled={primaryStart === undefined}
          aria-describedby={isHeating ? 'paired-secondary-hint' : undefined}
          onChange={(e) =>
            setSecondaryStart(
              e.target.value === '' ? undefined : Number(e.target.value),
            )
          }
        >
          {(!isEdit || secondaryStart === undefined) && (
            <option value="">Choose a time…</option>
          )}
          {secondaryOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {isHeating && (
          <p id="paired-secondary-hint" className="schedule-field__hint">
            Setback runs until your next Comfort period.
          </p>
        )}
      </div>

      <div className="schedule-modal__actions">
        <button
          type="button"
          className="schedule-modal__save"
          disabled={!canSave}
          onClick={() => onSave({ primaryStart, secondaryStart })}
        >
          {isEdit ? 'Update event' : 'Add event'}
        </button>
        <button
          type="button"
          className="schedule-modal__cancel"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      {isEdit && canDelete && (
        <div className="schedule-modal__delete">
          {confirmingDelete ? (
            <div className="schedule-modal__confirm" role="group">
              <span>Delete this event?</span>
              <button
                type="button"
                className="schedule-modal__confirm-yes"
                onClick={onDelete}
              >
                Delete
              </button>
              <button
                type="button"
                className="schedule-modal__confirm-no"
                onClick={() => setConfirmingDelete(false)}
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete event
            </button>
          )}
        </div>
      )}
    </ModalShell>
  );
}
